import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "goober";
import { Button } from "clutch/src/Button/Button.jsx";

import { readState } from "@/__main__/app-state.mjs";
import { postData } from "@/__main__/get-data.mjs";
import eventBus from "@/app/app-event-bus.mjs";
import { EVENT_ERROR } from "@/app/ErrorBoundary.jsx";
import getBearerToken from "@/feature-auth/utils/get-auth-request-header.mjs";
import { addPaymentMethod } from "@/feature-wallet/actions.mjs";
import { createPaymentMethod } from "@/feature-wallet/api.mjs";
import {
  BraintreeClientProvider,
  PayPalClientProvider,
  usePayPalClient,
} from "@/feature-wallet/BraintreeProvider.jsx";
import { CreatePaymentMethodModel } from "@/feature-wallet/models/create-payment-method.mjs";
import { PayPalTokenizeResponseModel } from "@/feature-wallet/models/paypal-tokenize-response.mjs";
import { DialogBody, DialogContent, DialogHeader } from "@/shared/Dialog.jsx";
import { ErrorOverlay } from "@/shared/ErrorOverlay.jsx";
import { devDebug, devError, devWarn } from "@/util/dev.mjs";

export function AddPaypalDialogView({
  onAddedPaymentMethod,
  onBack,
}: {
  onAddedPaymentMethod(paymentMethodId: string): void;
  onBack(): void;
}) {
  const onStatusChanged = useCallback(
    (state: AddPayPalPaymentMethodState) => {
      if (state.status === "success") {
        onAddedPaymentMethod(state.paymentMethodId);
      }
    },
    [onAddedPaymentMethod],
  );

  const { t } = useTranslation();

  return (
    <DialogContent>
      <DialogHeader
        title={["common:wallet.addPaymentMethod.paypal", "PayPal"]}
        onPressBack={onBack}
      />

      <PaypalDialogBody>
        <p className="type-form--button">
          {t(
            "common:wallet.addPaymentMethod.paypal.info",
            "You will be directed to PayPal to authorize your payment method, then you will be returned to Blitz to complete this purchase.",
          )}
        </p>

        <BraintreeClientProvider>
          <PayPalClientProvider>
            <AddPayPalPaymentMethodButton onStatusChanged={onStatusChanged} />
          </PayPalClientProvider>
        </BraintreeClientProvider>
      </PaypalDialogBody>
    </DialogContent>
  );
}

const PaypalDialogBody = styled(DialogBody)`
  p {
    padding-bottom: var(--sp-6);
  }

  button[data-emphasis="high"] {
    & {
      --bg: var(--shade0);
      --border: transparent;
      --spinner-color: var(--shade10);
      color: var(--shade7);
      background-image: none;
    }

    &:hover,
    &:focus {
      --bg: hsla(0deg 0% 100% / 0.85);
    }
  }
`;

type AddPayPalPaymentMethodState =
  | { status: "idle"; inProgress: false }
  | { status: "waitingForAuthorization"; inProgress: true }
  | { status: "creatingPaymentMethod"; inProgress: true }
  | { status: "success"; inProgress: false; paymentMethodId: string }
  | { status: "cancelled"; inProgress: false }
  | { status: "error"; inProgress: false; error: Error | unknown };

function AddPayPalPaymentMethodButton({
  onStatusChanged,
}: {
  onStatusChanged?: (state: AddPayPalPaymentMethodState) => void;
}) {
  const paypalClient = usePayPalClient();

  const [requestState, setRequestState] = useState<AddPayPalPaymentMethodState>(
    { status: "idle", inProgress: false },
  );

  useEffect(() => {
    onStatusChanged?.(requestState);
  }, [requestState, onStatusChanged]);

  const openPayPalPopup = useCallback(async () => {
    setRequestState({ status: "waitingForAuthorization", inProgress: true });

    try {
      const response = await paypalClient.tokenize({
        flow: "vault",
      });

      const result = PayPalTokenizeResponseModel(response, {
        failOnError: true,
      });

      if (result !== undefined) {
        devDebug("[paypal] tokenization successful", { nonce: result.nonce });

        setRequestState({ status: "creatingPaymentMethod", inProgress: true });

        const { testClock } = readState.volatile;

        const bearerToken = await getBearerToken();

        if (!bearerToken) return;

        const newPaymentMethod = await postData(
          createPaymentMethod({
            paymentMethodId: result.nonce,
            providerType: "BRAINTREE",
            testClock: testClock?.id,
          }),
          CreatePaymentMethodModel,
          undefined,
          { headers: { Authorization: bearerToken } },
        );

        if (newPaymentMethod instanceof Error) throw newPaymentMethod;

        setRequestState({
          status: "success",
          inProgress: false,
          paymentMethodId: newPaymentMethod.id,
        });

        addPaymentMethod(newPaymentMethod);
      } else {
        devWarn("[paypal] unexpected tokenization response", response);
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        if (err.name === "BraintreeError") {
          if ("code" in err && err.code === "PAYPAL_POPUP_CLOSED") {
            devDebug("[paypal] tokenization popup closed");
            setRequestState({ status: "cancelled", inProgress: false });
            return;
          }
        }
      }

      setRequestState({ status: "error", inProgress: false, error: err });

      devError(err);

      eventBus.emit(EVENT_ERROR, {
        error: err,
        tags: ["wallet", "link_paypal"],
      });
    }
  }, [paypalClient]);

  const { t } = useTranslation();

  return (
    <Button
      emphasis="high"
      onClick={openPayPalPopup}
      loading={paypalClient === null || requestState.inProgress}
      disabled={
        paypalClient === null ||
        requestState.inProgress ||
        requestState.status === "error"
      }
      block
    >
      {t("common:wallet.addPaymentMethod.paypal.connect", "Connect account")}

      {requestState.status === "error" && <ErrorOverlay />}
    </Button>
  );
}
